import type { HairlineFigure } from "./Hairline";
import {
  Cam,
  circ,
  facing,
  fillet,
  fit,
  poly,
  prism,
  proj,
  rad,
  rings,
  seg,
  unproj,
  type Ring,
  type Vec2,
} from "./iso";
import { spring, stepS } from "./motion";
import {
  disposer,
  flatDot,
  mk,
  place,
  pointer,
  put,
  register,
  solid,
  type FigureEls,
  type FigureHandle,
} from "./stage";

/**
 * Gears: a train of four toothed gears of different sizes lying on a board,
 * each on its hub, each meshing with the next. The pointer circling the lead
 * gear turns it: its angle follows the pointer's angle around its centre on a
 * spring, and every gear after it turns by its ratio and the other way, teeth
 * seated in its neighbour's gaps at every angle. A timing mark on the lead
 * gear is the one bright mark at rest; turned, the lead gear takes the bright
 * stroke. The slider is how many turns of the lead gear one circle makes.
 *
 * The pattern: a continuous input, as Turntable. One spring on the lead gear,
 * the rest worked out from it, and a hit test on the ground plane, which never
 * moves. A gear's outline is not convex, so its solid is written by hand.
 */

const M = 3,
  Z1 = 5,
  HUB = 3,
  CLEAR = 0.4,
  TAU = 2 * Math.PI;
/** The train: each gear's teeth, and the direction from each centre to the next. */
const N = [16, 10, 14, 8],
  DIR = [20, -25, 25],
  R = N.map((n) => (M * n) / 2);
const CTR: Vec2[] = [[0, 0]];
DIR.forEach((a, i) => {
  const [x, y] = CTR[i],
    d = R[i] + R[i + 1] + CLEAR;
  CTR.push([x + d * Math.cos(rad(a)), y + d * Math.sin(rad(a))]);
});
/** At rest: the lead gear's angle, its timing mark turned toward the viewer. */
const REST = 2.2;

/** A gear's outline in its own plane, counter-clockwise: n teeth with rounded tips and roots, narrow enough to seat in a neighbour's gaps at every angle, and each edge's outward normal. */
function outline(n: number, r: number) {
  const p = TAU / n,
    rt = r + 0.9 * M,
    rr = r - 1.15 * M,
    raw: Vec2[] = [];
  for (let k = 0; k < n; k++) {
    const c = k * p;
    for (const [d, a] of [
      [rr, c - 0.29 * p],
      [rt, c - 0.15 * p],
      [rt, c + 0.15 * p],
      [rr, c + 0.29 * p],
      [rr, c + 0.5 * p],
    ]) {
      raw.push([d * Math.cos(a), d * Math.sin(a)]);
    }
  }
  const pts = fillet(
    raw,
    raw.map(() => 0.9),
    2,
  );
  const nrm = pts.map((a, i): Vec2 => {
    const b = pts[(i + 1) % pts.length],
      dx = b[0] - a[0],
      dy = b[1] - a[1],
      l = Math.hypot(dx, dy) || 1;
    return [dy / l, -dx / l];
  });
  return { pts, nrm, rr };
}

/** Gear i + 1's angle when gear i stands at th: turned the other way by the ratio, a gap of it facing a tooth of gear i. */
const next = (i: number, th: number) =>
  rad(DIR[i]) +
  Math.PI -
  Math.PI / N[i + 1] +
  (rad(DIR[i]) - th) * (N[i] / N[i + 1]);

function mount({ stage, svg, read }: FigureEls, value: number): FigureHandle {
  const bag = disposer();
  let ratio = value;

  const C = Cam(45, 0.5, 1.85);
  const [bx0, by0, bx1, by1] = [-33, -33, 120, 37];
  fit(
    C,
    [
      [bx0, by0, -4],
      [bx1, by1, -4],
      [bx1, by0, -4],
      [bx0, by1, -4],
      [bx0, by0, Z1 + HUB],
      [bx1, by0, Z1 + HUB],
    ],
    200,
    166,
  );
  const P = proj(C),
    front = facing(C);

  const root = mk("g", {}, svg);
  const [br, bi] = rings(bx0, by0, bx1, by1, 8, 2);
  put(solid(root), prism(P, front, br, bi, -4, 0));

  // Back to front by centre: the gears never move from their axles, so this order holds.
  const gears = N.map((n, i) => ({
    i,
    n,
    c: CTR[i],
    ...outline(n, R[i]),
    drawn: NaN,
    base: null as SVGPathElement,
    edge: null as SVGPathElement,
    top: null as SVGPathElement,
    sil: null as SVGPathElement,
    crease: null as SVGPathElement,
    holes: null as SVGPathElement,
    dot: null as SVGEllipseElement,
  }));
  type Gear = (typeof gears)[number];
  for (const gr of gears
    .slice()
    .sort((a, b) => a.c[0] + a.c[1] - (b.c[0] + b.c[1]))) {
    const g = mk("g", {}, root),
      [cx, cy] = gr.c,
      at = (ring: Ring, z: number) =>
        poly(ring.map((q) => P(cx + q.u, cy + q.v, z)));
    gr.base = mk("path", { class: "fo" }, g);
    gr.edge = mk("path", { class: "nf sil" }, g);
    gr.top = mk("path", { class: "fo" }, g);
    gr.sil = mk("path", { class: "nf sil" }, g);
    gr.crease = mk("path", { class: "nf" }, g);
    gr.holes = mk("path", { class: "nf" }, g);
    mk("path", { d: at(circ(gr.rr - 2.6, 48), Z1), class: "nf lo" }, g);
    const rh = Math.max(3.2, R[gr.i] * 0.28),
      hub = circ(rh, 32).map((q) => ({ ...q, u: q.u + cx, v: q.v + cy }));
    put(
      solid(g),
      prism(
        P,
        front,
        hub,
        circ(rh - 0.8, 32).map((q) => ({ ...q, u: q.u + cx, v: q.v + cy })),
        Z1,
        Z1 + HUB,
      ),
    );
    mk("path", { d: at(circ(rh * 0.42, 24), Z1 + HUB), class: "nf" }, g);
    if (gr.i === 0) gr.dot = flatDot(g, C, 1.3, "dot");
  }

  /** A gear at angle th: its foot and walls, the edges under the walls, its lid, and the lid's edges, bright where nothing stands below them. */
  function draw(gr: Gear, th: number) {
    if (th === gr.drawn) return;
    gr.drawn = th;
    const cs = Math.cos(th),
      sn = Math.sin(th),
      [cx, cy] = gr.c;
    const w = gr.pts.map(
      ([u, v]): Vec2 => [cx + u * cs - v * sn, cy + u * sn + v * cs],
    );
    const bot = w.map(([x, y]) => P(x, y, 0)),
      top = w.map(([x, y]) => P(x, y, Z1));
    let walls = "",
      edge = "",
      sil = "",
      crease = "";
    const faces = gr.nrm.map(([nu, nv]) =>
      front({ u: 0, v: 0, nu: nu * cs - nv * sn, nv: nu * sn + nv * cs }),
    );
    for (let i = 0; i < w.length; i++) {
      const j = (i + 1) % w.length;
      // Where the wall turns from facing the viewer to facing away, its side is the silhouette, as a cylinder's is.
      if (faces[i] !== faces[(i + w.length - 1) % w.length])
        edge += seg(top[i], bot[i]);
      if (faces[i]) {
        walls += poly([bot[i], bot[j], top[j], top[i]]);
        edge += seg(bot[i], bot[j]);
        crease += seg(top[i], top[j]);
      } else sil += seg(top[i], top[j]);
    }
    gr.base.setAttribute("d", poly(bot) + walls);
    gr.edge.setAttribute("d", edge);
    gr.top.setAttribute("d", poly(top));
    gr.sil.setAttribute("d", sil);
    gr.crease.setAttribute("d", crease);
    // The large gears' web is lightened by five holes, which is what shows them turning.
    const rh = Math.max(3.2, R[gr.i] * 0.28),
      ring = (rh + gr.rr - 2.6) / 2,
      hr = (gr.rr - 2.6 - rh) * 0.28;
    let holes = "";
    if (gr.n >= 14)
      for (let k = 0; k < 5; k++) {
        const a = th + ((k + 0.5) * TAU) / 5;
        holes += poly(
          circ(hr, 20).map((q) =>
            P(cx + ring * Math.cos(a) + q.u, cy + ring * Math.sin(a) + q.v, Z1),
          ),
        );
      }
    gr.holes.setAttribute("d", holes);
    if (gr.dot) place(gr.dot, P(cx + ring * cs, cy + ring * sn, Z1));
  }

  const lead = spring(REST);
  function drawAll() {
    let th = lead.x;
    gears.forEach((gr, i) => {
      if (i > 0) th = next(i - 1, th);
      draw(gr, th);
    });
  }
  const B = register(stage, (dt) => {
    const moving = stepS(lead, dt);
    drawAll();
    return moving;
  });
  bag.add(() => B.unregister());

  /** The bright: the lead gear while it is turned; at rest, its timing mark. */
  function light(on: boolean) {
    gears[0].edge.classList.toggle("hi", on);
    gears[0].sil.classList.toggle("hi", on);
    gears[0].dot.classList.toggle("m", on);
  }
  const deg = (t: number) =>
    String(((Math.round((t * 180) / Math.PI) % 360) + 360) % 360).padStart(
      3,
      "0",
    ) + "°";

  let act = false,
    last = 0,
    turns = 0;
  /** The pointer's angle round the lead gear on the ground plane, unwound, sets where the lead gear is going. */
  function aim([sx, sy]: Vec2) {
    const [wx, wy] = unproj(C, sx, sy, Z1),
      dx = wx - CTR[0][0],
      dy = wy - CTR[0][1];
    if (Math.hypot(dx, dy) < 3) return;
    const phi = Math.atan2(dy, dx);
    if (!act) {
      act = true;
      turns = phi + TAU * Math.round((lead.t / ratio - phi) / TAU);
      light(true);
    } else {
      const d = phi - last;
      turns += d - TAU * Math.round(d / TAU);
    }
    last = phi;
    lead.t = ratio * turns;
    read.textContent = deg(lead.t);
    B.wake();
  }
  function leave() {
    if (!act) return;
    act = false;
    lead.t = REST + TAU * Math.round((lead.t - REST) / TAU);
    light(false);
    read.textContent = "rest";
    B.wake();
  }

  drawAll();
  bag.add(pointer(stage, { move: aim, leave }));
  bag.add(() => svg.replaceChildren());

  return {
    set: (v) => {
      ratio = v;
      if (act) {
        lead.t = ratio * turns;
        read.textContent = deg(lead.t);
        B.wake();
      }
    },
    destroy: () => bag.dispose(),
  };
}

export const GEARS: HairlineFigure = {
  name: "gears",
  means:
    "A train of four gears on a board: circling the lead gear turns it, and every gear after it turns in step, the other way.",
  range: [0.5, 1, 2],
  tour: [[113, 109], [106, 151], [202, 136], null],
  mount,
};
